import { Component, ElementRef, Input, Output, EventEmitter, OnInit, ViewChild, OnDestroy,ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
// import TokenSimulationEngineModule from 'bpmn-js-token-simulation/lib/engine';  
// import TokenSimulationViewerModule from 'bpmn-js-token-simulation/lib/viewer';
// import BpmnModeler      from 'bpmn-js/lib/Modeler';
import NavigatedViewer from 'bpmn-js/lib/NavigatedViewer';
import html2canvas from 'html2canvas';
import { BaseServiceService } from '../../services/base-service.service';
import { AppData } from "../../helpers/app-data";
import { Observable } from 'rxjs';

/**
 * Item normalizado para el motor genérico de anotaciones (textAnnotation embebido en el XML),
 * compartido entre Repetitivas y Cuellos de Botella (cada uno construye su propia lista a partir
 * de sus datos crudos: `repetitive_tasks` / `hallazgos`).
 */
interface AnalisisNotaItem {
  elementId: string;
  texto: string;
  colorFill?: string;
  colorStroke?: string;
}

@Component({
  selector: 'app-diagram-viewer',
  templateUrl: "./diagram-viewer.component.html",
  styleUrls: ['./diagram-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DiagramViewerComponent implements OnInit, OnDestroy {
  @ViewChild('diagramContainer', { static: true }) 
  private containerRef: ElementRef | any = null;
  
  @Input() xmlContent: string | any = null;
  @Input() readOnly: boolean = true;
  @Input() tooltipProperties: TooltipProperty[] = [];
  @Input() showLegend: boolean = true;
  @Input() legendTitle: string = 'Leyenda de Estados';
  @Input() legendItems: LegendItem[] = [
    { color: '#4CAF50', label: 'Finalizado' },
    { color: '#FFC107', label: 'Pendiente' },
    { color: '#9E9E9E', label: 'Fallido' },
    { color: '#F44336', label: 'Cancelado' }
  ];
  @Input() showTooltips: boolean = true;
  @Input() hideWatermark: boolean = false;
  
  private viewer: NavigatedViewer | any = null;
  private tokenSimulation: any;
  private toggleMode: any;
  public isSimulationActive: boolean = false;
  private paramToken?: string;
  private paramIdProceso?: string;
  private headers: HttpHeaders | any = null;

  // --- Análisis de actividades repetitivas ---
  private analisisData: any = null;
  private posicionesGuardadas: Map<number, { x: number; y: number; w: number; h: number }> | null = null;
  private analisisXmlActual: string = '';
  public mostrarBotonAnalisis: boolean = false;
  public analisisVisible: boolean = false;

  // Emite el estado del análisis solo cuando cambia, en vez de recalcularlo en cada ciclo
  // de detección de cambios (evita que un getter en el template recree bindings constantemente).
  @Output() analisisStateChange = new EventEmitter<{ disponible: boolean; visible: boolean }>();

  // --- Análisis de Cuellos de Botella (punto 6: mismo motor de anotaciones que Repetitivas) ---
  private analisisCBData: any = null;
  private analisisCBXmlActual: string = '';
  public mostrarBotonAnalisisCB: boolean = false;
  public analisisCBVisible: boolean = false;
  private readonly PREFIX_NOTAS_REPETITIVAS = 'BSP_REP_';
  private readonly PREFIX_NOTAS_CB = 'BSP_CB_';

  @Output() analisisCBStateChange = new EventEmitter<{ disponible: boolean; visible: boolean }>();

  get enableAgenteRepetitivas(): boolean {
    return !!AppData.ENABLE_AGENTE_REPETITIVAS;
  }

  get enableAgenteCuellosBotella(): boolean {
    return !!AppData.ENABLE_AGENTE_CUELLOS_BOTELLA;
  }

  private emitAnalisisState(): void {
    const visible = AppData.MODO_VISUALIZACION_ANALISIS_IA === 'notas'
      ? this.analisisVisible
      : this.semaforoVisiblePara('repetitivas');
    this.analisisStateChange.emit({
      disponible: this.mostrarBotonAnalisis && this.enableAgenteRepetitivas,
      visible
    });
  }

  /** Paleta de colores por categoría de hallazgo (misma convención que el Diagramador). */
  private colorPorCategoriaCB(categoria: string | undefined): string {
    const paleta: Record<string, string> = {
      CB01: '#4D6EF7',
      CB02: '#9b5571',
      CB03: '#ecb180',
    };
    return paleta[(categoria || '').toUpperCase()] || '#5D4037';
  }

  private tinteClaroCB(colorHex: string): string {
    const tintes: Record<string, string> = {
      '#4D6EF7': '#f3e5f5',
      '#9b5571': '#fce4ec',
      '#ecb180': '#fff3e0',
    };
    return tintes[colorHex] || '#efebe9';
  }

  /** Aplana los hallazgos (cada uno puede involucrar varios elementos) en items normalizados. */
  private construirItemsNotasCB(): AnalisisNotaItem[] {
    const items: AnalisisNotaItem[] = [];
    (this.analisisCBData?.hallazgos || []).forEach((h: any) => {
      const color = this.colorPorCategoriaCB(h.categoria);
      (h.elementos_involucrados || []).forEach((e: any) => {
        if (!e?.id) return;
        items.push({
          elementId: e.id,
          texto: `[${h.categoria}] ${this.truncarTexto(h.patron + ': '+ h.justificacion || '', 500)}`,
          colorFill: this.tinteClaroCB(color),
          colorStroke: color,
        });
      });
    });
    return items;
  }

  private construirItemsNotasRepetitivas(tasks: any[]): AnalisisNotaItem[] {
    return (tasks || []).map((t: any, index: number) => ({
      elementId: t.user_task_id || '',
      texto: `TR${index + 1}: ${this.truncarTexto(t.patron || '', 500)}`,
      colorFill: '#ffe0b2',
      colorStroke: '#e65100',
    }));
  }

  private truncarTexto(texto: string, maxLength: number): string {
    return (texto || '').length > maxLength ? (texto || '').substring(0, maxLength) + '...' : (texto || '');
  }

  private emitAnalisisCBState(): void {
    const visible = AppData.MODO_VISUALIZACION_ANALISIS_IA === 'notas'
      ? this.analisisCBVisible
      : this.semaforoVisiblePara('cuellos-botella');
    this.analisisCBStateChange.emit({
      disponible: this.mostrarBotonAnalisisCB && this.enableAgenteCuellosBotella,
      visible
    });
  }

  // ── Visualización "semáforo con tooltip" (mismo mecanismo que el Diagramador) ──────────────
  // Opción ADICIONAL a la visualización "notas": colorea las tareas con hallazgos manipulando
  // directamente el fill/stroke del SVG ya renderizado (sin tocar el XML), y muestra un tooltip
  // al pasar el mouse con la justificación del agente activo. Activación INDEPENDIENTE por
  // agente, pero solo un agente puede estar activo a la vez (mismo RF que el modo "notas").
  private semaforoAgentesActivos = new Set<string>();
  /** Overlays de tooltip actualmente mostrados de forma permanente, por elemento. */
  private semaforoOverlaysActivos = new Map<string, string>();

  /** Definición de agentes para el modo "semáforo", construida a partir de los datos ya cargados. */
  private get agentesSemaforo(): {
    id: string;
    label: string;
    color: string;
    obtenerJustificacion: (elementId: string) => string | null | undefined;
    obtenerColor?: (elementId: string) => string | null | undefined;
  }[] {
    return [
      {
        id: 'repetitivas',
        label: 'Actividades repetidas',
        color: '#e65100',
        obtenerJustificacion: (elementId: string) =>
          this.analisisData?.repetitive_tasks?.find((t: any) => t.user_task_id === elementId)?.patron,
      },
      {
        id: 'cuellos-botella',
        label: 'Cuellos de botella',
        color: '#4D6EF7',
        obtenerJustificacion: (elementId: string) => {
          const hallazgos = this.hallazgosCBPorTarea(elementId);
          if (!hallazgos.length) return null;
          return hallazgos.map((h: any) => `[${h.categoria}] ${h.patron}: ${h.justificacion}`).join('\n\n');
        },
        obtenerColor: (elementId: string) => {
          const hallazgos = this.hallazgosCBPorTarea(elementId);
          return hallazgos.length ? this.colorPorCategoriaCB(hallazgos[0].categoria) : undefined;
        },
      },
    ];
  }

  private hallazgosCBPorTarea(elementId: string): any[] {
    const hallazgos: any[] = [];
    (this.analisisCBData?.hallazgos || []).forEach((h: any) => {
      if ((h.elementos_involucrados || []).some((e: any) => e?.id === elementId)) {
        hallazgos.push(h);
      }
    });
    return hallazgos;
  }

  /** true si el semáforo del agente indicado está actualmente visible sobre el diagrama. */
  semaforoVisiblePara(agenteId: string): boolean {
    return this.semaforoAgentesActivos.has(agenteId);
  }

  async toggleSemaforoIA(agenteId: string): Promise<void> {
    if (this.semaforoAgentesActivos.has(agenteId)) {
      this.semaforoAgentesActivos.delete(agenteId);
    } else {
      // Solo un agente visible a la vez (misma regla que el modo "notas").
      this.semaforoAgentesActivos.clear();
      this.semaforoAgentesActivos.add(agenteId);
    }
    this.actualizarMarcadoresSemaforo();
    this.emitAnalisisState();
    this.emitAnalisisCBState();
  }

  /** Colorea las tareas con hallazgo activo y muestra su tooltip de forma permanente (no solo al pasar el mouse). */
  private actualizarMarcadoresSemaforo(): void {
    if (!this.viewer) return;
    const elementRegistry = this.viewer.get('elementRegistry') as any;
    const overlays = this.viewer.get('overlays') as any;
    const agentesActivos = this.agentesSemaforo.filter((a) => this.semaforoAgentesActivos.has(a.id));

    elementRegistry.getAll().forEach((element: any) => {
      const gfx = elementRegistry.getGraphics(element);
      const visual = gfx?.querySelector('.djs-visual');
      const shape = visual?.querySelector('rect, circle, polygon, path');

      if (shape) {
        const agenteConHallazgo = agentesActivos.find((a) => !!a.obtenerJustificacion(element.id)) || null;

        if (agenteConHallazgo) {
          if (!shape.hasAttribute('data-bsp-semaforo-activo')) {
            shape.setAttribute('data-bsp-semaforo-fill-original', shape.style.fill || '');
            shape.setAttribute('data-bsp-semaforo-stroke-original', shape.style.stroke || '');
            shape.setAttribute('data-bsp-semaforo-activo', '1');
          }
          const color = agenteConHallazgo.obtenerColor?.(element.id) || agenteConHallazgo.color;
          shape.style.fill = color;
          shape.style.stroke = color;
        } else if (shape.hasAttribute('data-bsp-semaforo-activo')) {
          shape.style.fill = shape.getAttribute('data-bsp-semaforo-fill-original') || '';
          shape.style.stroke = shape.getAttribute('data-bsp-semaforo-stroke-original') || '';
          shape.removeAttribute('data-bsp-semaforo-fill-original');
          shape.removeAttribute('data-bsp-semaforo-stroke-original');
          shape.removeAttribute('data-bsp-semaforo-activo');
        }
      }

      // Tooltip permanente: se muestra apenas se activa el semáforo y no se oculta hasta desactivarlo.
      const overlayExistente = this.semaforoOverlaysActivos.get(element.id);
      if (overlayExistente) {
        overlays.remove(overlayExistente);
        this.semaforoOverlaysActivos.delete(element.id);
      }
      const contenido = agentesActivos.length ? this.crearContenidoTooltipSemaforo(element) : null;
      if (contenido) {
        const cantidadHallazgos = agentesActivos.filter((a) => !!a.obtenerJustificacion(element.id)).length;
        const overlayId = overlays.add(element.id, 'bsp-semaforo-tooltip', {
          position: { top: -(60 + cantidadHallazgos * 45), left: -20 },
          html: contenido,
        });
        this.semaforoOverlaysActivos.set(element.id, overlayId);
      }
    });

    this.evitarSuperposicionTooltips(overlays);
  }

  /**
   * Separa verticalmente los tooltips que quedan superpuestos entre sí, sin perder la relación
   * visual con su tarea (cada tooltip ya indica el nombre de la tarea a la que pertenece).
   */
  private evitarSuperposicionTooltips(overlays: any): void {
    const canvas = this.viewer.get('canvas') as any;
    const scale = canvas.viewbox().scale || 1;
    const margen = 8;

    const entradas = Array.from(this.semaforoOverlaysActivos.values())
      .map((overlayId) => overlays.get(overlayId))
      .filter((overlay: any) => !!overlay?.htmlContainer)
      .map((overlay: any) => ({ overlay, rect: overlay.htmlContainer.getBoundingClientRect() }))
      .sort((a: any, b: any) => a.rect.top - b.rect.top);

    const colocados: { top: number; bottom: number; left: number; right: number }[] = [];

    entradas.forEach(({ overlay, rect }: any) => {
      let top = rect.top;
      let desplazamiento = 0;
      colocados.forEach((r) => {
        const seSolapan = top < r.bottom + margen && rect.bottom > r.top - margen && rect.left < r.right && rect.right > r.left;
        if (seSolapan) {
          desplazamiento = Math.max(desplazamiento, r.bottom + margen - top);
        }
      });

      if (desplazamiento > 0) {
        overlay.position.top = (overlay.position.top || 0) + desplazamiento / scale;
        overlays._updateOverlay(overlay);
        top += desplazamiento;
      }

      colocados.push({ top, bottom: top + rect.height, left: rect.left, right: rect.right });
    });
  }

  private crearContenidoTooltipSemaforo(element: any): string | null {
    const bloques = this.agentesSemaforo
      .filter((a) => this.semaforoAgentesActivos.has(a.id))
      .map((a) => {
        const justificacion = a.obtenerJustificacion(element.id);
        if (!justificacion) return null;
        const color = a.obtenerColor?.(element.id) || a.color;
        const justificacionHtml = this.escapeHtml(justificacion).replace(/\n/g, '<br>');
        return `
          <div style="border-left: 3px solid ${color}; padding: 4px 8px; margin-bottom: 6px;">
            <strong style="color: ${color};">${this.escapeHtml(a.label)}</strong>
            <div style="font-size: 12px; color: #333;">${justificacionHtml}</div>
          </div>`;
      })
      .filter((bloque): bloque is string => !!bloque);

    if (!bloques.length) return null;
    const nombreTarea = element.businessObject?.name || element.id;
    // Ancho responsive: se ajusta al contenido con un piso y un techo relativo al viewport,
    // para que un hallazgo corto no quede en una columna angosta y uno largo no se salga de pantalla.
    return `
      <div style="background: #ffffff; border: 1px solid #e1e1e1; border-radius: 4px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.25); padding: 6px;
                  width: max-content; min-width: 220px; max-width: min(420px, 90vw);
                  overflow-wrap: break-word; word-break: break-word; box-sizing: border-box;
                  font-family: system-ui, -apple-system, sans-serif;">
        <div style="font-weight: 600; font-size: 12px; color: #111; margin-bottom: 4px;
                    padding-bottom: 4px; border-bottom: 1px solid #eee;">
          🔹 ${this.escapeHtml(nombreTarea)}
        </div>
        ${bloques.join('')}
      </div>`;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private baseService: BaseServiceService
  ) {}

  ngOnInit(): void {
    // Obtener parámetros de la URL (query params)
    this.paramToken = this.baseService.getParamValue("Token");
    this.paramIdProceso = this.baseService.getParamValue("IdProceso");

    // Configurar headers para la API
    this.headers = new HttpHeaders({
      'X-APIKey': AppData.API_KEY,  
      'Authorization': 'Bearer ' + this.paramToken  
    });

  // const simulationEngine = (TokenSimulationEngineModule as any).default || TokenSimulationEngineModule;
  // const simulationViewer = (TokenSimulationViewerModule as any).default || TokenSimulationViewerModule;
  // const CustomModule = {
  //   __init__: [
  //     [ 'eventBus', 'toggleMode', function(eventBus, toggleMode) {
  //       eventBus.on('tokenSimulation.toggleMode', e => console.log('toggleMode:', e.active));
  //     } ]
  //   ]
  // } as any;

    // Inicializar el viewer
    this.viewer = new NavigatedViewer({
      container: this.containerRef.nativeElement,
      additionalModules: [
        // simulationEngine,
        // simulationViewer,
        // CustomModule
      ]
    });

    // Reaplica los marcadores y tooltips permanentes del modo "semáforo" después de cada
    // (re)importación, ya que bpmn-js recrea los nodos SVG.
    this.viewer.on('import.done', () => {
      this.semaforoOverlaysActivos.clear();
      if (this.semaforoAgentesActivos.size > 0) {
        this.actualizarMarcadoresSemaforo();
      }
    });

    // Cargar el diagrama desde la API
    if (this.paramIdProceso) {
      this.getProcesoConfig().subscribe((response) => {
        const xmlContent = response.Data.Configuracion;
        if (xmlContent) {
          this.procesarAnalisisYCargar(xmlContent);
        }
      });
    } else if (this.xmlContent) {
      this.procesarAnalisisYCargar(this.xmlContent);
    }
  }

  /**
   * Detecta si el XML recibido contiene los comentarios BSP_REPETITIVE_ANALYSIS y/o
   * BSP_BOTTLENECK_ANALYSIS, extrae los datos de cada análisis (y las posiciones
   * guardadas de sus anotaciones), limpia los comentarios del XML y carga el diagrama
   * resultante en el viewer.
   */
  private procesarAnalisisYCargar(xmlDesdeBD: string): void {
    this.semaforoAgentesActivos.clear();
    const regex = /<!--\s*BSP_REPETITIVE_ANALYSIS:\s*([\s\S]*?)\s*-->/;
    const match = xmlDesdeBD.match(regex);

    this.analisisData = null;
    this.posicionesGuardadas = null;
    this.analisisXmlActual = '';
    this.analisisVisible = false;
    this.mostrarBotonAnalisis = false;

    if (match) {
      try {
        const parsed = JSON.parse(match[1]);
        this.analisisData = parsed;

        if (parsed._bsp_positions && Object.keys(parsed._bsp_positions).length) {
          this.posicionesGuardadas = new Map(
            Object.entries(parsed._bsp_positions).map(
              ([k, v]: [string, any]) => [Number(k), v as { x: number; y: number; w: number; h: number }]
            )
          );
        }
      } catch (e) {
        console.error('Error al parsear datos de análisis de actividades repetitivas:', e);
        this.analisisData = null;
      }
    }

    const regexCB = /<!--\s*BSP_BOTTLENECK_ANALYSIS:\s*([\s\S]*?)\s*-->/;
    const matchCB = xmlDesdeBD.match(regexCB);

    this.analisisCBData = null;
    this.analisisCBXmlActual = '';
    this.analisisCBVisible = false;
    this.mostrarBotonAnalisisCB = false;

    if (matchCB) {
      try {
        this.analisisCBData = JSON.parse(matchCB[1]);
      } catch (e) {
        console.error('Error al parsear datos de análisis de cuellos de botella:', e);
        this.analisisCBData = null;
      }
    }

    const xmlLimpio = xmlDesdeBD
      .replace(/\s*<!--\s*BSP_REPETITIVE_ANALYSIS:[\s\S]*?-->/g, '')
      .replace(/\s*<!--\s*BSP_BOTTLENECK_ANALYSIS:[\s\S]*?-->/g, '')
      .trim();

    this.mostrarBotonAnalisis = this.analisisData !== null && (this.analisisData.repetitive_tasks?.length > 0);
    this.mostrarBotonAnalisisCB = this.analisisCBData !== null && (this.analisisCBData.hallazgos?.length > 0);
    this.emitAnalisisState();
    this.emitAnalisisCBState();

    this.loadDiagram(xmlLimpio);
  }

  async toggleAnalisis(): Promise<void> {
    if (AppData.MODO_VISUALIZACION_ANALISIS_IA !== 'notas') {
      await this.toggleSemaforoIA('repetitivas');
      return;
    }
    if (!this.analisisData) {
      return;
    }

    try {
      // Solo un análisis "notas" visible a la vez: si se va a mostrar Repetitivas
      // y Cuellos de Botella está visible, se oculta primero.
      if (!this.analisisVisible && this.analisisCBVisible) {
        await this.ocultarNotasCB();
      }

      const result = await this.viewer.saveXML({ format: true });
      const xmlActual = result.xml;
      const items = this.construirItemsNotasRepetitivas(this.analisisData.repetitive_tasks || []);

      if (this.analisisVisible) {
        // OCULTAR: guardar XML actual con posiciones, luego quitar y reimportar
        this.analisisXmlActual = xmlActual;
        const xmlLimpio = this.quitarAnalisisDelXML(xmlActual, items, this.PREFIX_NOTAS_REPETITIVAS);
        await this.viewer.importXML(xmlLimpio);
        this.analisisVisible = false;
      } else {
        // MOSTRAR: calcular/restaurar posiciones, aplicar y reimportar
        let savedPositions: Map<number, { x: number; y: number; w: number; h: number }> | null = null;
        if (this.analisisXmlActual) {
          savedPositions = this.extraerPosicionesDeXML(this.analisisXmlActual, items, this.PREFIX_NOTAS_REPETITIVAS);
        } else if (this.posicionesGuardadas) {
          savedPositions = this.posicionesGuardadas;
        }

        const xmlConAnalisis = this.aplicarAnalisisAlXML(xmlActual, items, this.PREFIX_NOTAS_REPETITIVAS, savedPositions);
        this.analisisXmlActual = xmlConAnalisis;
        await this.viewer.importXML(xmlConAnalisis);
        this.analisisVisible = true;
      }

      const canvas = this.viewer.get('canvas') as any;
      canvas.zoom('fit-viewport');
      this.emitAnalisisState();
      this.emitAnalisisCBState();
    } catch (error) {
      console.error('Error al alternar el análisis de actividades repetitivas:', error);
    }
  }

  /** Oculta las notas de Repetitivas sin pasar por el toggle público (usado para la exclusión mutua). */
  private async ocultarNotasRepetitivas(): Promise<void> {
    const result = await this.viewer.saveXML({ format: true });
    const xmlActual = result.xml;
    const items = this.construirItemsNotasRepetitivas(this.analisisData?.repetitive_tasks || []);
    this.analisisXmlActual = xmlActual;
    const xmlLimpio = this.quitarAnalisisDelXML(xmlActual, items, this.PREFIX_NOTAS_REPETITIVAS);
    await this.viewer.importXML(xmlLimpio);
    this.analisisVisible = false;
  }

  /** Igual que `toggleAnalisis`, pero para los hallazgos de Cuellos de Botella (punto 6). */
  async toggleAnalisisCuellosBotella(): Promise<void> {
    if (AppData.MODO_VISUALIZACION_ANALISIS_IA !== 'notas') {
      await this.toggleSemaforoIA('cuellos-botella');
      return;
    }
    if (!this.analisisCBData) {
      return;
    }

    try {
      // Solo un análisis "notas" visible a la vez: si se va a mostrar Cuellos de Botella
      // y Repetitivas está visible, se oculta primero.
      if (!this.analisisCBVisible && this.analisisVisible) {
        await this.ocultarNotasRepetitivas();
      }

      const result = await this.viewer.saveXML({ format: true });
      const xmlActual = result.xml;
      const items = this.construirItemsNotasCB();

      if (this.analisisCBVisible) {
        this.analisisCBXmlActual = xmlActual;
        const xmlLimpio = this.quitarAnalisisDelXML(xmlActual, items, this.PREFIX_NOTAS_CB);
        await this.viewer.importXML(xmlLimpio);
        this.analisisCBVisible = false;
      } else {
        const savedPositions = this.analisisCBXmlActual
          ? this.extraerPosicionesDeXML(this.analisisCBXmlActual, items, this.PREFIX_NOTAS_CB)
          : null;

        const xmlConAnalisis = this.aplicarAnalisisAlXML(xmlActual, items, this.PREFIX_NOTAS_CB, savedPositions);
        this.analisisCBXmlActual = xmlConAnalisis;
        await this.viewer.importXML(xmlConAnalisis);
        this.analisisCBVisible = true;
      }

      const canvas = this.viewer.get('canvas') as any;
      canvas.zoom('fit-viewport');
      this.emitAnalisisCBState();
      this.emitAnalisisState();
    } catch (error) {
      console.error('Error al alternar el análisis de cuellos de botella:', error);
    }
  }

  /** Oculta las notas de Cuellos de Botella sin pasar por el toggle público (usado para la exclusión mutua). */
  private async ocultarNotasCB(): Promise<void> {
    const result = await this.viewer.saveXML({ format: true });
    const xmlActual = result.xml;
    const items = this.construirItemsNotasCB();
    this.analisisCBXmlActual = xmlActual;
    const xmlLimpio = this.quitarAnalisisDelXML(xmlActual, items, this.PREFIX_NOTAS_CB);
    await this.viewer.importXML(xmlLimpio);
    this.analisisCBVisible = false;
  }

  private aplicarAnalisisAlXML(
    xml: string,
    items: AnalisisNotaItem[],
    prefix: string = this.PREFIX_NOTAS_REPETITIVAS,
    savedPositions?: Map<number, { x: number; y: number; w: number; h: number }> | null
  ): string {
    // Primero limpiar cualquier análisis visual previo (idempotente)
    xml = this.quitarAnalisisDelXML(xml, items, prefix);

    let processAnnotations = '';
    let diAnnotations = '';

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');

      items.forEach((item: AnalisisNotaItem, index: number) => {
        if (!item.elementId) return;

        const shapes = xmlDoc.getElementsByTagName('bpmndi:BPMNShape');
        let bounds: { x: number; y: number; w: number; h: number } | null = null;

        for (let i = 0; i < shapes.length; i++) {
          if (shapes[i].getAttribute('bpmnElement') === item.elementId) {
            shapes[i].setAttribute('fill', item.colorFill || '#ffe0b2');
            shapes[i].setAttribute('stroke', item.colorStroke || '#e65100');
            const b = shapes[i].getElementsByTagName('dc:Bounds')[0];
            if (b) {
              bounds = {
                x: parseFloat(b.getAttribute('x') || '0'),
                y: parseFloat(b.getAttribute('y') || '0'),
                w: parseFloat(b.getAttribute('width') || '100'),
                h: parseFloat(b.getAttribute('height') || '80')
              };
            }
            break;
          }
        }

        if (!bounds) return;

        const annId = `${prefix}ANN_${index}`;
        const assocId = `${prefix}ASSOC_${index}`;
        const shapeId = `${prefix}SHAPE_${index}`;
        const edgeId = `${prefix}EDGE_${index}`;

        const safeText = item.texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const saved = savedPositions?.get(index);
        const annX = saved ? saved.x : bounds.x + bounds.w + 20;
        const annY = saved ? saved.y : bounds.y;
        const annW = saved ? saved.w : 230;
        const annH = saved ? saved.h : 60;

        processAnnotations += `
    <bpmn2:textAnnotation id="${annId}">
      <bpmn2:text>${safeText}</bpmn2:text>
    </bpmn2:textAnnotation>
    <bpmn2:association id="${assocId}" associationDirection="None" sourceRef="${item.elementId}" targetRef="${annId}" />`;

        diAnnotations += `
    <bpmndi:BPMNShape id="${shapeId}" bpmnElement="${annId}">
      <dc:Bounds x="${annX}" y="${annY}" width="${annW}" height="${annH}" />
    </bpmndi:BPMNShape>
    <bpmndi:BPMNEdge id="${edgeId}" bpmnElement="${assocId}">
      <di:waypoint x="${bounds.x + bounds.w}" y="${bounds.y + bounds.h / 2}" />
      <di:waypoint x="${annX}" y="${annY + annH / 2}" />
    </bpmndi:BPMNEdge>`;
      });

      xml = new XMLSerializer().serializeToString(xmlDoc);
    } catch (error) {
      console.error('Error al aplicar análisis al XML:', error);
    }

    if (processAnnotations) {
      xml = xml.replace(/(<\/bpmn2:process>|<\/bpmn:process>)/, processAnnotations + '\n  $1');
      xml = xml.replace(/(<\/bpmndi:BPMNPlane>)/, diAnnotations + '\n  $1');
    }

    return xml;
  }

  private quitarAnalisisDelXML(xml: string, items: AnalisisNotaItem[], prefix: string = this.PREFIX_NOTAS_REPETITIVAS): string {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      const BSP = prefix;

      const stripById = (tagName: string) => {
        const els = xmlDoc.getElementsByTagName(tagName);
        for (let i = els.length - 1; i >= 0; i--) {
          if ((els[i].getAttribute('id') || '').startsWith(BSP)) {
            els[i].parentNode?.removeChild(els[i]);
          }
        }
      };

      ['bpmn2:textAnnotation', 'bpmn:textAnnotation',
        'bpmn2:association', 'bpmn:association',
        'bpmndi:BPMNShape', 'bpmndi:BPMNEdge'].forEach(stripById);

      const taskIds = items.map((item: AnalisisNotaItem) => item.elementId).filter(Boolean);
      if (taskIds.length) {
        const shapes = xmlDoc.getElementsByTagName('bpmndi:BPMNShape');
        for (let i = 0; i < shapes.length; i++) {
          if (taskIds.includes(shapes[i].getAttribute('bpmnElement') || '')) {
            ['fill', 'stroke', 'bioc:fill', 'bioc:stroke',
              'color:background-color', 'color:border-color'].forEach(attr =>
                shapes[i].removeAttribute(attr)
              );
          }
        }
      }

      return new XMLSerializer().serializeToString(xmlDoc);
    } catch (error) {
      console.error('Error al quitar análisis del XML:', error);
      return xml;
    }
  }

  private extraerPosicionesDeXML(
    xml: string,
    items: AnalisisNotaItem[],
    prefix: string = this.PREFIX_NOTAS_REPETITIVAS
  ): Map<number, { x: number; y: number; w: number; h: number }> {
    const positions = new Map<number, { x: number; y: number; w: number; h: number }>();
    try {
      const xmlDoc = new DOMParser().parseFromString(xml, 'text/xml');
      items.forEach((_: AnalisisNotaItem, index: number) => {
        const shapes = xmlDoc.getElementsByTagName('bpmndi:BPMNShape');
        for (let i = 0; i < shapes.length; i++) {
          if (shapes[i].getAttribute('id') === `${prefix}SHAPE_${index}`) {
            const b = shapes[i].getElementsByTagName('dc:Bounds')[0];
            if (b) {
              positions.set(index, {
                x: parseFloat(b.getAttribute('x') || '0'),
                y: parseFloat(b.getAttribute('y') || '0'),
                w: parseFloat(b.getAttribute('width') || '230'),
                h: parseFloat(b.getAttribute('height') || '60')
              });
            }
            break;
          }
        }
      });
    } catch (e) {
      console.error('Error al extraer posiciones del XML:', e);
    }
    return positions;
  }

  getProcesoConfig(): Observable<any> {
    console.log(AppData.API_KEY + 'apikey');
    
  
    return this.http.get<any>(
      `${AppData.API_URL}Proceso/GetProcesoConfiguracion?Id=${Number(this.paramIdProceso)}`, {headers:this.headers }
    );
  }

  async loadDiagram(xmlContent: string): Promise<void> {
    try {

      await this.viewer.importXML(xmlContent);
      
      // try {
      //   // this.tokenSimulation = this.viewer.get('tokenSimulation');
      //   // this.toggleMode = this.viewer.get('toggleMode');

      //   console.log('Token simulation module loaded successfully');
      // } catch (simError) {
      //   console.error('Token simulation not available:', simError);
      //   this.tokenSimulation = null;
      // }

      // Ajustar zoom para que se vea completo
      const canvas = this.viewer.get('canvas') as any;
      canvas.zoom('fit-viewport');

      this.enableMouseNavigation();
      
      // Agregar tooltips solo si está habilitado
      if (this.showTooltips) {
        this.setupTooltips();
      }

      // this.setupSimulationEvents();

    } catch (error) {
      console.error('Error loading diagram:', error);
    }
  }

  private setupTooltips(): void {
    const elementRegistry = this.viewer.get('elementRegistry') as any;
    const overlays = this.viewer.get('overlays') as any;
    
    let currentTooltip: any = null;
    
    // Obtener todos los elementos del diagrama
    const elements = elementRegistry.getAll();
    
    elements.forEach((element: any) => {
      // Solo agregar tooltips a elementos que no sean conexiones
      if (element.type !== 'bpmn:SequenceFlow' &&
          element.type !== 'bpmn:Participant' && 
          element.type !== 'bpmn:Collaboration' && 
          element.type !== 'label' && 
          element.type !== 'bpmn:Lane' &&
          element.businessObject) {
        const gfx = elementRegistry.getGraphics(element);
        
        if (gfx) {
          // Evento mouseenter
          gfx.addEventListener('mouseenter', (event: MouseEvent) => {
            // Remover tooltip anterior si existe
            if (currentTooltip) {
              overlays.remove(currentTooltip);
              currentTooltip = null;
            }
            
            // Crear contenido del tooltip
            const tooltipContent = this.createTooltipContent(element);
            const position = this.calculateTooltipPosition(element);
            // Crear el tooltip
            currentTooltip = overlays.add(element.id, 'tooltip', {
              position: position,
              html: tooltipContent
            });
          });
          
          // Evento mouseleave
          gfx.addEventListener('mouseleave', () => {
            if (currentTooltip) {
              overlays.remove(currentTooltip);
              currentTooltip = null;
            }
          });
        }
      }
    });
  }

  private calculateTooltipPosition(element: any): { top: number, left: number } {
    // Calcular la altura aproximada del tooltip basándose en el contenido
    const contentLines = this.countTooltipContentLines(element);
    let tooltipHeight: number;
    // Definir alturas basándose en el número de líneas
    if (contentLines <= 1) {
      tooltipHeight = 40;   // Tooltip pequeño (solo header + 1 línea)
    } else if (contentLines <= 3) {
      tooltipHeight = 160;  // Tooltip mediano
    } else if (contentLines <= 5) {
      tooltipHeight = 200;  // Tooltip grande
    } else {
      tooltipHeight = 240;  // Tooltip muy grande
    }
    const baseOffset = 15;
    
    return {
      top: -(tooltipHeight + baseOffset),
      left: -120
    };
  }

  private countTooltipContentLines(element: any): number {
    const businessObject = element.businessObject;
    let lines = 0;
    if (businessObject.$attrs && this.tooltipProperties.length > 0) {
      const attrs = businessObject.$attrs;
      
      this.tooltipProperties.forEach(prop => {
        const value = attrs[prop.key];
        if (value) {
          lines++;
        }
      });
    }
    if (businessObject.documentation && businessObject.documentation.length > 0) {
      lines++;
    }
    return lines;
  }

  private createTooltipContent(element: any): string {
    const businessObject = element.businessObject;
    
    let content = `
      <div class="bpmn-tooltip">
        <div class="tooltip-header">
          <strong>${this.getElementTypeName(element.type)}</strong>
        </div>
        <div class="tooltip-body">
    `;
        // Agregar información básica del elemento BPMN
    // if (businessObject.name) {
    //   content += `<div><strong>Nombre:</strong> ${businessObject.name}</div>`;
    // }
    
    // if (businessObject.id) {
    //   content += `<div><strong>ID:</strong> ${businessObject.id}</div>`;
    // }
    // Procesar las propiedades configurables desde el XML
    if (businessObject.$attrs && this.tooltipProperties.length > 0) {
      const attrs = businessObject.$attrs;
      
      this.tooltipProperties.forEach(prop => {
        const value = attrs[prop.key];
        if (value) {
          const formattedValue = this.formatValue(value, prop.type, prop.format);
          content += `<div><strong>${prop.label}:</strong> ${formattedValue}</div>`;
        }
      });
    }
    
    // Agregar documentación del businessObject si existe
    if (businessObject.documentation && businessObject.documentation.length > 0) {
      content += `<div><strong>Documentación BPMN:</strong> ${businessObject.documentation[0].text}</div>`;
    }    
    content += `
        </div>
      </div>
    `;
    
    return content;
  }
  private formatValue(value: any, type: string, format?: string): string {
    switch (type) {
      case 'date':
        try {
          const date = new Date(value);
          if (format) {
            // Si se especifica un formato personalizado, úsalo
            return date.toLocaleDateString('es-ES', JSON.parse(format));
          } else {
            // Formato por defecto
            return date.toLocaleString('es-ES', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        } catch (error) {
          return value;
        }
      
      case 'number':
        return parseFloat(value).toLocaleString('es-ES');
      
      case 'boolean':
        return value === 'true' ? 'Sí' : 'No';
      
      case 'text':
      default:
        return value;
    }
  }
  private getElementTypeName(type: string): string {
    const typeNames: { [key: string]: string } = {
      'bpmn:StartEvent': 'Evento de Inicio',
      'bpmn:EndEvent': 'Evento de Fin',
      'bpmn:UserTask': 'Tarea de Usuario',
      'bpmn:ServiceTask': 'Tarea de Servicio',
      'bpmn:ScriptTask': 'Tarea de Script',
      'bpmn:ManualTask': 'Tarea Manual',
      'bpmn:BusinessRuleTask': 'Tarea de Regla de Negocio',
      'bpmn:SendTask': 'Tarea de Envío',
      'bpmn:ReceiveTask': 'Tarea de Recepción',
      'bpmn:ExclusiveGateway': 'Gateway Exclusivo',
      'bpmn:ParallelGateway': 'Gateway Paralelo',
      'bpmn:InclusiveGateway': 'Gateway Inclusivo',
      'bpmn:EventBasedGateway': 'Gateway Basado en Eventos',
      'bpmn:SubProcess': 'Sub-proceso',
      'bpmn:CallActivity': 'Actividad de Llamada',
      'bpmn:IntermediateThrowEvent': 'Evento Intermedio de Lanzamiento',
      'bpmn:IntermediateCatchEvent': 'Evento Intermedio de Captura',
      'bpmn:BoundaryEvent': 'Evento de Frontera',
      'bpmn:Participant': 'Pool'
    };
    
    return typeNames[type] || type.replace('bpmn:', '');
  }
   private setupSimulationEvents(): void {
    // Escuchar eventos de simulación
    this.viewer.on('tokenSimulation.finished', () => {
      console.log('Simulation finished');
      this.isSimulationActive = false;
    });

    this.viewer.on('tokenSimulation.paused', () => {
      console.log('Simulation paused');
    });

    this.viewer.on('tokenSimulation.resumed', () => {
      console.log('Simulation resumed');
    });
  }
    // Métodos de control de simulación
  startSimulation(): void {
    if (this.tokenSimulation) {
      try {
        this.tokenSimulation.start();
        this.isSimulationActive = true;
        console.log('Simulation started');
      } catch (error) {
        console.error('Error starting simulation:', error);
      }
    } else {
      console.warn('Token simulation not available');
    }
  }

  pauseSimulation(): void {
    if (this.tokenSimulation) {
      try {
        this.tokenSimulation.pause();
        console.log('Simulation paused');
      } catch (error) {
        console.error('Error pausing simulation:', error);
      }
    }
  }

  resetSimulation(): void {
    if (this.tokenSimulation) {
      try {
        this.tokenSimulation.reset();
        this.isSimulationActive = false;
        console.log('Simulation reset');
      } catch (error) {
        console.error('Error resetting simulation:', error);
      }
    }
  }

  toggleSimulationMode(): void {
    if (this.toggleMode) {
      try {
        this.toggleMode.toggleMode();
        console.log('Simulation mode toggled');
      } catch (error) {
        console.error('Error toggling simulation mode:', error);
      }
    }
  }

  private enableMouseNavigation(): void {
    const canvas = this.viewer.get('canvas') as any;
    const container = this.containerRef.nativeElement;

    // Habilitar zoom con la rueda del mouse
    container.addEventListener('wheel', (event: WheelEvent) => {
      event.preventDefault();
      
      const zoomLevel = canvas.zoom();
      const factor = event.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(4, zoomLevel * factor));
      
      canvas.zoom(newZoom, {
        x: event.offsetX,
        y: event.offsetY
      });
    });

    // Habilitar arrastre con el mouse
    let isDragging = false;
    let lastX = 0;
    let lastY = 0;

    container.addEventListener('mousedown', (event: MouseEvent) => {
      if (event.button === 0) { // Click izquierdo
        isDragging = true;
        lastX = event.clientX;
        lastY = event.clientY;
        container.style.cursor = 'grabbing';
      }
    });

    container.addEventListener('mousemove', (event: MouseEvent) => {
      if (isDragging) {
        const deltaX = event.clientX - lastX;
        const deltaY = event.clientY - lastY;
        
        const viewbox = canvas.viewbox();
        canvas.viewbox({
          x: viewbox.x - deltaX,
          y: viewbox.y - deltaY,
          width: viewbox.width,
          height: viewbox.height
        });
        
        lastX = event.clientX;
        lastY = event.clientY;
      }
    });

    container.addEventListener('mouseup', () => {
      isDragging = false;
      container.style.cursor = 'default';
    });

    container.addEventListener('mouseleave', () => {
      isDragging = false;
      container.style.cursor = 'default';
    });
  }

  // Métodos públicos para controlar zoom
  zoomIn(): void {
    const canvas = this.viewer.get('canvas') as any;
    const currentZoom = canvas.zoom();
    canvas.zoom(Math.min(4, currentZoom * 1.2));
  }

  zoomOut(): void {
    const canvas = this.viewer.get('canvas') as any;
    const currentZoom = canvas.zoom();
    canvas.zoom(Math.max(0.1, currentZoom * 0.8));
  }

  fitToScreen(): void {
    const canvas = this.viewer.get('canvas') as any;
    canvas.zoom('fit-viewport');
  }

  resetZoom(): void {
    const canvas = this.viewer.get('canvas') as any;
    canvas.zoom(1);
  }

  /**
   * Captura tal cual se ve en pantalla el diagrama junto con los tooltips del semáforo (modo popup),
   * usando html2canvas sobre el contenedor del viewer (incluye el SVG y los overlays HTML).
   */
  private async exportarCapturaConTooltips(filename: string): Promise<void> {
    try {
      const canvas = this.viewer.get('canvas') as any;
      const container = canvas.getContainer() as HTMLElement;

      const resultCanvas = await html2canvas(container, {
        backgroundColor: '#ffffff',
        scale: 3,
        useCORS: true,
      });

      resultCanvas.toBlob((blob) => {
        if (blob) {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
        }
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('Error al exportar la captura del diagrama con tooltips:', error);
      throw error;
    }
  }

  async exportAsImage(filename: string = 'diagrama-proceso.png'): Promise<void> {
    // Modo popup con hallazgos activos: se toma una captura real del diagrama (SVG + tooltips en
    // pantalla), en vez de reconstruir el SVG a mano, para que la imagen sea idéntica a lo que se ve.
    // El modo "notas" no se toca y sigue usando el export original.
    if (AppData.MODO_VISUALIZACION_ANALISIS_IA !== 'notas' && this.semaforoAgentesActivos.size > 0) {
      await this.exportarCapturaConTooltips(filename);
      return;
    }

    try {
      // Factor de escala para aumentar la resolución (2x = doble resolución)
      const scale = 3;
      
      // Obtener el SVG del diagrama directamente desde el contenedor
      const canvas = this.viewer.get('canvas') as any;
      const container = canvas.getContainer();
      
      // Buscar el SVG directamente en el contenedor
      const svgElement = container.querySelector('svg');
      
      if (!svgElement) {
        console.error('No se encontró el elemento SVG');
        return;
      }

      // Obtener las dimensiones del viewport actual
      const bbox = svgElement.getBoundingClientRect();
      
      // Crear un canvas temporal con dimensiones escaladas
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = bbox.width * scale;
      tempCanvas.height = bbox.height * scale;

      // Clonar el SVG para no afectar el original
      const clonedSvg = svgElement.cloneNode(true) as SVGElement;
      
      // Asegurar que el SVG tiene las dimensiones correctas (sin escalar aún)
      clonedSvg.setAttribute('width', bbox.width.toString());
      clonedSvg.setAttribute('height', bbox.height.toString());

      // Serializar el SVG
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(clonedSvg);

      // Crear una imagen desde el SVG
      const img = new Image();
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        const ctx = tempCanvas.getContext('2d');
        if (ctx) {
          // Aplicar escalado para mejor calidad
          ctx.scale(scale, scale);
          
          // Fondo blanco
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, bbox.width, bbox.height);
          
          // Dibujar el SVG
          ctx.drawImage(img, 0, 0, bbox.width, bbox.height);

          // Convertir a imagen y descargar
          tempCanvas.toBlob((blob) => {
            if (blob) {
              const link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = filename;
              link.click();
              URL.revokeObjectURL(link.href);
            }
          }, 'image/png', 1.0); // Calidad máxima
        }
        URL.revokeObjectURL(url);
      };

      img.onerror = (error) => {
        console.error('Error al cargar la imagen SVG:', error);
        URL.revokeObjectURL(url);
      };

      img.src = url;
    } catch (error) {
      console.error('Error al exportar el diagrama como imagen:', error);
      throw error;
    }
  }

  ngOnDestroy(): void {
    if (this.viewer) {
      this.viewer.destroy();
    }
  }
}
export interface TooltipProperty {
  key: string;
  label: string; 
  type: 'text' | 'date' | 'number' | 'boolean';
  format?: string; 
}
export interface LegendItem {
  color: string;
  label: string;
}